import { useQuery } from '@tanstack/react-query'
import { Link, Outlet, useRouterState } from '@tanstack/react-router'
import { listReservationsOptions } from '@/entities/capacity'
import { listFleetSignalsOptions, listFleetUnitsOptions } from '@/entities/fleet'
import { getCallsOptions } from '@/entities/metering'
import { listScenariosOptions } from '@/entities/scenario'
import { listSipAccountsOptions } from '@/entities/telephony'
import { listVoicesOptions } from '@/entities/voice'
import { useLogout } from '@/features/auth'
import { ThemeSwitcher, useThemeEffect } from '@/features/theme'
import { useAuthToken } from '@/shared/api'
import { cn } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Icon, type IconName } from '@/shared/ui/icon'

interface NavItem {
  to: string
  icon: IconName
  label: string
  count?: number | undefined
}

interface NavGroup {
  title: string
  items: NavItem[]
}

function NavLink({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <Link
      to={item.to}
      className={cn(
        'group flex items-center gap-3 rounded-control p-1.5 pr-3 transition-colors',
        active ? 'bg-card' : 'hover:bg-card/60',
      )}
    >
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-control transition-colors',
          active ? 'bg-grad-tile text-white' : 'bg-card text-muted group-hover:text-app-fg',
        )}
      >
        <Icon name={item.icon} size={18} />
      </span>
      <span
        className={cn(
          'flex-1 truncate text-body-sm transition-colors',
          active ? 'font-medium text-app-fg' : 'text-muted group-hover:text-app-fg',
        )}
      >
        {item.label}
      </span>
      {item.count !== undefined && item.count > 0 && (
        <span className="flex h-6 min-w-6 items-center justify-center rounded-full border border-outline px-1.5 text-caption text-muted">
          {item.count}
        </span>
      )}
    </Link>
  )
}

export function AppShell() {
  useThemeEffect()
  const token = useAuthToken()
  const logout = useLogout()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const authed = token != null
  const units = useQuery({ ...listFleetUnitsOptions(), enabled: authed })
  // Счётчик — живые заявки: терминальные заводятся навсегда и в счётчике доски
  // не значат ничего.
  const reservations = useQuery({
    ...listReservationsOptions({ query: { active: true } }),
    enabled: authed,
  })
  // Счётчик — только активные сигналы: снятые лежат историей и внимания уже
  // не требуют.
  const signals = useQuery({
    ...listFleetSignalsOptions({ query: { active: 'true' } }),
    enabled: authed,
    refetchInterval: 30_000,
  })
  const accounts = useQuery({ ...listSipAccountsOptions(), enabled: authed })
  const calls = useQuery({ ...getCallsOptions(), enabled: authed, refetchInterval: 15_000 })
  const voices = useQuery({ ...listVoicesOptions(), enabled: authed })
  const scenarios = useQuery({ ...listScenariosOptions(), enabled: authed })

  if (!authed) {
    return (
      <main className="flex min-h-full items-center justify-center p-6">
        <Outlet />
      </main>
    )
  }

  const groups: NavGroup[] = [
    {
      title: 'Флот',
      items: [
        {
          to: '/signals',
          icon: 'bell',
          label: 'Требует внимания',
          count: signals.data?.data?.length,
        },
        { to: '/capacity', icon: 'bolt', label: 'Ёмкость', count: reservations.data?.data?.length },
        { to: '/units', icon: 'server', label: 'Юниты', count: units.data?.data?.length },
        { to: '/cells', icon: 'grid', label: 'Ячейки' },
      ],
    },
    {
      title: 'Телефония',
      items: [
        { to: '/telephony/profiles', icon: 'phone', label: 'Профили' },
        { to: '/telephony/pools', icon: 'layers', label: 'Пулы' },
        {
          to: '/telephony/accounts',
          icon: 'id-card',
          label: 'Учётки',
          count: accounts.data?.data?.length,
        },
        { to: '/telephony/leases', icon: 'link', label: 'Аренды' },
      ],
    },
    {
      title: 'Метеринг',
      items: [
        { to: '/calls', icon: 'phone-call', label: 'Звонки', count: calls.data?.live?.length },
        { to: '/usage', icon: 'chart', label: 'Usage' },
      ],
    },
    {
      title: 'Демо',
      items: [
        { to: '/voices', icon: 'mic', label: 'Голоса', count: voices.data?.data?.length },
        {
          to: '/scenarios',
          icon: 'pen',
          label: 'Сценарии',
          count: scenarios.data?.data?.length,
        },
        { to: '/demo-call', icon: 'play', label: 'Демо-звонок' },
      ],
    },
  ]

  return (
    <div className="flex min-h-full">
      <aside className="sticky top-0 flex h-screen w-[264px] shrink-0 flex-col gap-3 p-3">
        <div className="flex items-center gap-3 rounded-panel bg-panel p-3">
          <span className="relative flex size-11 shrink-0 items-center justify-center rounded-control bg-grad-tile text-white">
            <Icon name="phone-call" size={20} />
            <span className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-panel bg-ok" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-body-sm font-medium">Caller Engine</p>
            <p className="text-caption text-faint">админка оператора</p>
          </div>
          <ThemeSwitcher />
        </div>

        <nav className="flex-1 overflow-y-auto rounded-panel bg-panel p-3">
          {groups.map((group) => (
            <div key={group.title} className="mb-4 last:mb-0">
              <p className="mb-1.5 px-2 text-caption font-medium uppercase tracking-[0.12em] text-faint">
                {group.title}
              </p>
              <div className="flex flex-col gap-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    item={item}
                    active={pathname === item.to || pathname.startsWith(`${item.to}/`)}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-2 rounded-panel bg-panel p-3">
          <Button className="flex-1" onClick={logout}>
            <Icon name="logout" size={18} />
            Выйти
          </Button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 p-3 pl-0">
        <div className="min-h-full rounded-panel bg-panel/60 p-6">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
